import React, { useState, useEffect } from 'react';
import {
    Modal,
    Text,
    Box,
    Textarea,
    Button,
    ActionIcon,
    Tooltip,
    Group,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import ImpersonationService from '../../services/ImpersonationService';
import { MODAL_SIZES } from '../../theme/mantineTheme';

const STORAGE_KEY = 'onBehalfOfRecentUpns';

interface OnBehalfOfStorageDialogProps {
    open: boolean;
    onClose: () => void;
    onSelectUpn: (upn: string) => void;
}

export const OnBehalfOfStorageDialog: React.FC<OnBehalfOfStorageDialogProps> = ({ open, onClose, onSelectUpn }) => {
    const [upns, setUpns] = useState<string[]>([]);
    const [json, setJson] = useState<string>('[]');
    const [keyName] = useState(STORAGE_KEY);

    const refresh = () => {
        const list = ImpersonationService.getRecentUpns();
        setUpns(list);
        setJson(JSON.stringify(list, null, 2));
    };

    useEffect(() => {
        if (open) refresh();
    }, [open]);

    const handleRemove = (upn: string) => {
        const next = ImpersonationService.getRecentUpns().filter((u) => u !== upn);
        ImpersonationService.setRecentUpns(next);
        refresh();
    };

    const handleClear = () => {
        ImpersonationService.setRecentUpns([]);
        refresh();
    };

    return (
        <Modal opened={open} onClose={onClose} title={<Tooltip label="OnBehalfOfStorageDialog" withArrow position="top"><span style={{ cursor: 'help' }}>On-behalf-of storage</span></Tooltip>} size={MODAL_SIZES.form}>
            <Text size="sm" c="dimmed" mb="xs">
                Previously used UPNs (successful entities/me). Stored in localStorage.
            </Text>
            {upns.length > 0 && (
                <Box mb="md">
                    {upns.map((upn) => (
                        <Group
                            key={upn}
                            gap="sm"
                            align="center"
                            wrap="nowrap"
                            py={6}
                            mih={32}
                        >
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => {
                                    onSelectUpn(upn);
                                    onClose();
                                }}
                                aria-label={`Use ${upn} as on-behalf-of UPN`}
                                miw={52}
                                style={{ flexShrink: 0 }}
                            >
                                Use
                            </Button>
                            <Text
                                size="sm"
                                ff="monospace"
                                truncate
                                flex={1}
                                miw={0}
                                title={upn}
                            >
                                {upn}
                            </Text>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                aria-label={`Remove ${upn}`}
                                onClick={() => handleRemove(upn)}
                                style={{ flexShrink: 0 }}
                            >
                                <IconX size={16} />
                            </ActionIcon>
                        </Group>
                    ))}
                </Box>
            )}
            <Box mb="md">
                <Text size="xs" c="dimmed">
                    Key:
                </Text>
                <Text size="sm" component="pre" ff="monospace" fz="xs">
                    {keyName}
                </Text>
            </Box>
            <Textarea
                value={json}
                onChange={(e) => setJson(e.currentTarget.value)}
                readOnly
                minRows={6}
                maxRows={12}
                ff="monospace"
                styles={{ input: { fontSize: '0.8rem' } }}
            />
            <Group mt="md" justify="flex-end" gap="sm">
                <Button variant="outline" onClick={handleClear}>
                    Clear history
                </Button>
                <Button onClick={onClose}>Close</Button>
            </Group>
        </Modal>
    );
};
